import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  input,
  output,
  viewChild,
} from '@angular/core';
import { Popover, PopoverModule } from 'primeng/popover';

/**
 * A popover anchored to whatever opened it, with projected content.
 *
 *   <button (click)="filters.toggle($event)">Filters</button>
 *   <sh-popover #filters styleClass="my-popover">...</sh-popover>
 *
 * `show(event, target?)` anchors to `target` (default the event's current
 * target). `opened` / `closed` fire when it appears and when it goes. The
 * popover is appended to the body by default (`appendTo`); the arrow follows
 * its anchor (`_primeng-overrides.scss`).
 */
@Component({
  selector: 'sh-popover',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [PopoverModule],
  template: `
    <p-popover
      [styleClass]="styleClass()"
      [appendTo]="appendTo()"
      (onShow)="opened.emit()"
      (onHide)="closed.emit()"
    >
      <ng-content></ng-content>
    </p-popover>
  `,
})
export class ShPopoverComponent {
  /** Central class hook on the popover (e.g. `page-filter-popover`). */
  readonly styleClass = input<string>();
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');

  readonly opened = output<void>();
  readonly closed = output<void>();

  private readonly _popover = viewChild.required(Popover);

  toggle(event: Event, target?: EventTarget | null): void {
    this._popover().toggle(event, target);
  }

  show(event: Event, target?: EventTarget | null): void {
    this._popover().show(event, target);
  }

  hide(): void {
    this._popover().hide();
  }

  // PrimeNG marks a click inside the popover as a "self click" and clears
  // the mark in its document click listener. A control inside that stops the
  // click (a multiselect option does) leaves the mark set, so the next
  // outside click only clears it and the popover takes a second click to
  // close. Clear a stale mark on any press outside, before its click lands.
  @HostListener('document:pointerdown', ['$event'])
  protected onDocumentPointerDown(event: PointerEvent): void {
    const popover = this._popover();
    if (!popover.overlayVisible) return;
    const target = event.target as Node | null;
    if (target && popover.container?.contains(target)) return;
    popover.selfClick = false;
  }
}
